
import * as React from "react"
import { ChevronUpIcon, ChevronDownIcon } from "lucide-react"
import { cn } from "../../lib/utils"

interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange" | "size"> {
  value?: number
  onChange?: (value: number) => void
  min?: number
  max?: number
  step?: number
  size?: "sm" | "default"
}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ className, value = 5, onChange, min, max, step = 1, size = "default", ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(value)

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = Number.parseInt(e.target.value) || 0
      setInternalValue(newValue)
      onChange?.(newValue)
    }

    const handleIncrement = () => {
      const newValue = internalValue + step
      if (max === undefined || newValue <= max) {
        setInternalValue(newValue)
        onChange?.(newValue)
      }
    }

    const handleDecrement = () => {
      const newValue = internalValue - step
      if (min === undefined || newValue >= min) {
        setInternalValue(newValue)
        onChange?.(newValue)
      }
    }

    React.useEffect(() => {
      setInternalValue(value)
    }, [value])

    return (
      <div className="relative inline-flex">
        <input
          type="number"
          className={cn(
            "flex items-center justify-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-8 text-center",
            size === "default" ? "h-9" : "h-8",
            className,
          )}
          value={internalValue}
          onChange={handleInputChange}
          min={min}
          max={max}
          step={step}
          ref={ref}
          {...props}
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col">
          <button
            type="button"
            onClick={handleIncrement}
            className="flex h-3 w-4 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
            disabled={max !== undefined && internalValue >= max}
            tabIndex={-1}
          >
            <ChevronUpIcon className="h-2.5 w-2.5" />
          </button>
          <button
            type="button"
            onClick={handleDecrement}
            className="flex h-3 w-4 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
            disabled={min !== undefined && internalValue <= min}
            tabIndex={-1}
          >
            <ChevronDownIcon className="h-2.5 w-2.5" />
          </button>
        </div>
      </div>
    )
  },
)

NumberInput.displayName = "NumberInput"

export { NumberInput }
